import React, { FC, Suspense, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import styled from 'styled-components';
import { Dialog } from 'src/components/widgets/dialog';
import { showErrorNotification, showSuccessNotification } from 'src/components/widgets/notificationToaster';
import { AdvancedSelect } from 'src/components/widgets/select';
import { SkeletonBlock } from 'src/components/widgets/skeleton';
import { stringFormat, T } from 'src/shared/helpers';
import { moveProductsToCategoryDialogAssignMutation } from './__generated__/moveProductsToCategoryDialogAssignMutation.graphql';
import { moveProductsToCategoryDialogQuery } from './__generated__/moveProductsToCategoryDialogQuery.graphql';
import { ProductToCategorize } from './categorizeProductsDialog';

const hierarchyQuery = graphql`
    query moveProductsToCategoryDialogQuery {
        merchantProductCategoryHierarchy {
            macroCategories {
                id
                name
                categories {
                    id
                    name
                }
            }
        }
    }
`;

const assignMutation = graphql`
    mutation moveProductsToCategoryDialogAssignMutation($categoryID: String!, $productID: String!) {
        assignProductToCategory(categoryID: $categoryID, productID: $productID) {
            id
        }
    }
`;

const Description = styled.p`
    margin: 0 0 16px;
    color: #57534e;
    font-size: 13px;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 6px;
    color: #57534e;
    font-size: 12px;
    font-weight: 600;
`;

interface MoveProductsToCategoryDialogProps {
    products: ProductToCategorize[];
    onClose: () => void;
    onMoved: () => void;
}

interface CategorySelectProps {
    categoryID: string;
    disabled: boolean;
    onChange: (categoryID: string) => void;
}

type CategoryOption = {
    readonly label: string;
    readonly value: string;
};

type CategoryGroup = {
    readonly label: string;
    readonly options: CategoryOption[];
};

const CategorySelect: FC<CategorySelectProps> = ({ categoryID, disabled, onChange }) => {
    const data = useLazyLoadQuery<moveProductsToCategoryDialogQuery>(hierarchyQuery, {}, { fetchPolicy: 'network-only' });
    const macroCategories = data.merchantProductCategoryHierarchy.macroCategories.filter((macroCategory) => macroCategory !== null);
    const categoryGroups: CategoryGroup[] = macroCategories
        .filter((macroCategory) => macroCategory.categories.length > 0)
        .map((macroCategory) => ({
            label: macroCategory.name,
            options: macroCategory.categories.map((category) => ({ label: category.name, value: category.id }))
        }));
    const categoryOptions = categoryGroups.flatMap((group) => group.options);
    const selectedCategory = categoryOptions.find((category) => category.value === categoryID) ?? null;
    const hasCategories = categoryOptions.length > 0;

    return (
        <AdvancedSelect<CategoryOption, false, CategoryGroup>
            inputId='move-products-category'
            options={categoryGroups}
            value={selectedCategory}
            onChange={(category) => onChange(category?.value ?? '')}
            isDisabled={!hasCategories || disabled}
            isClearable
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            menuPosition='fixed'
            placeholder={hasCategories ? T._('Find by category...', 'Admin') : T._('No categories available', 'Admin')}
            styles={{ menuPortal: (provided) => ({ ...provided, zIndex: 50 }) }}
        />
    );
};

const CategorySelectLoading = () => <SkeletonBlock $height='32px' $radius='6px' aria-hidden='true' />;

export const MoveProductsToCategoryDialog: FC<MoveProductsToCategoryDialogProps> = ({ products, onClose, onMoved }) => {
    const [assignProduct] = useMutation<moveProductsToCategoryDialogAssignMutation>(assignMutation);
    const [categoryID, setCategoryID] = useState('');
    const [isMoving, setIsMoving] = useState(false);

    const moveProduct = (product: ProductToCategorize) =>
        new Promise<void>((resolve, reject) => {
            assignProduct({
                variables: { categoryID, productID: product.id },
                onCompleted: () => resolve(),
                onError: reject
            });
        });

    const handleMove = async () => {
        if (!categoryID || isMoving) return;
        setIsMoving(true);
        try {
            await Promise.all(products.map(moveProduct));
            showSuccessNotification({
                message:
                    products.length === 1
                        ? T._('Product moved to the selected category.', 'Admin')
                        : stringFormat(T._('{0} products moved to the selected category.', 'Admin'), String(products.length))
            });
            onMoved();
        } catch {
            showErrorNotification({ message: T._('Some products could not be moved. Please try again.', 'Admin') });
            setIsMoving(false);
        }
    };

    return (
        <Dialog
            size='small'
            title={T._('Move to category', 'Admin')}
            onClose={onClose}
            buttons={[
                {
                    label: isMoving ? T._('Moving...', 'Admin') : T._('Move', 'Admin'),
                    disabled: !categoryID || isMoving,
                    onClick: handleMove
                },
                {
                    label: T._('Cancel', 'Admin'),
                    outline: true,
                    disabled: isMoving,
                    onClick: onClose
                }
            ]}
        >
            <Description>
                {products.length === 1
                    ? T._('Select the new category for this product.', 'Admin')
                    : stringFormat(T._('Select the new category for these {0} products.', 'Admin'), String(products.length))}
            </Description>
            <Label htmlFor='move-products-category'>{T._('Category', 'Admin')}</Label>
            <Suspense fallback={<CategorySelectLoading />}>
                <CategorySelect categoryID={categoryID} disabled={isMoving} onChange={setCategoryID} />
            </Suspense>
        </Dialog>
    );
};
